import { useEffect } from "react";
import { useDispatch } from "react-redux";

import {
  fetchAllCountries,
  fetchAllCurrencies,
  fetchContainerMaterials,
  fetchContainerOptions,
  fetchContainerTypes,
  fetchFuelTypes,
} from "./utils/public-api-utils";

import {
  setAllCountries,
  setAllCurrencies,
  setContainerMaterials,
  setContainerOptions,
  setContainerTypes,
  setFuelTypes,
} from "./redux/reducers/public/public-action";

import { refreshAuthToken } from "./lib/api/client";
import { getAuthToken, getRefreshToken } from "./lib/api/tokenStore";
import { hydrateProfile } from "./features/auth/completeAuth";
import { ascendingSort } from "./utils/common-utils";

/**
 * Boot work: restore the session, then load the public reference data.
 *
 * `Promise.allSettled`, not `all` — the old code called `setLoading(true)`
 * from its catch, so one failed request left the app on the loader forever.
 */
const PublicAuthAPIs = ({ setLoading }) => {
  const dispatch = useDispatch();

  useEffect(() => {
    bootstrap();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const valueOf = (result, fallback = []) =>
    result.status === "fulfilled" ? result.value : fallback;

  /**
   * The auth token lives in sessionStorage and dies with the tab, while the
   * refresh token persists. So a new tab opens holding only the refresh
   * token and would look signed out until something exchanged it — that
   * exchange is this.
   */
  const restoreSession = async () => {
    if (getAuthToken() || !getRefreshToken()) return;

    const authToken = await refreshAuthToken();
    if (authToken) await hydrateProfile();
  };

  const bootstrap = async () => {
    await restoreSession();

    const [
      containerTypes,
      fuelTypes,
      containerOptions,
      containerMaterials,
      countries,
      currencies,
    ] = await Promise.allSettled([
      fetchContainerTypes(),
      fetchFuelTypes(),
      fetchContainerOptions(),
      fetchContainerMaterials(),
      fetchAllCountries(),
      fetchAllCurrencies(),
    ]);

    dispatch(setContainerTypes(valueOf(containerTypes)));
    dispatch(setFuelTypes(valueOf(fuelTypes)));
    dispatch(setContainerOptions(ascendingSort(valueOf(containerOptions), "id")));
    dispatch(
      setContainerMaterials(ascendingSort(valueOf(containerMaterials), "id"))
    );
    dispatch(setAllCountries(valueOf(countries)));
    dispatch(setAllCurrencies(valueOf(currencies)));

    setLoading(false);
  };

  return <div></div>;
};

export default PublicAuthAPIs;
